package com.siriusxm.pia.operations.components

import androidx.compose.runtime.Composable
import com.siriusxm.unifiedcontent.cmc.Monitor
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * A card component displaying a single DataDog monitor's status and details.
 *
 * Shows:
 * - Status indicator (colored circle)
 * - Monitor name (linked to DataDog if URL provided)
 * - Priority badge (P1-P5 if set)
 *
 * @param monitor The monitor to display
 * @param datadogBaseUrl Optional base URL for linking to DataDog (e.g., "https://app.datadoghq.com")
 * @param onClick Optional click handler for the card
 */
@Composable
fun MonitorCard(
    monitor: Monitor,
    datadogBaseUrl: String? = "https://app.datadoghq.com",
    onClick: (() -> Unit)? = null
) {
    Div({
        classes(OperationsStyles.monitorCard)
        if (onClick != null) {
            style {
                cursor("pointer")
            }
            this.onClick { onClick() }
        }
    }) {
        // Status indicator
        MonitorStatusIndicator(monitor.status)

        // Monitor name with optional link to DataDog
        Div({
            classes(OperationsStyles.monitorName)
        }) {
            val monitorUrl = monitor.id?.let { id ->
                datadogBaseUrl?.let { "$it/monitors/$id" }
            }

            if (monitorUrl != null) {
                A(href = monitorUrl, {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                    style {
                        property("color", "inherit")
                        property("text-decoration", "none")
                    }
                    this.onClick { it.stopPropagation() }
                }) {
                    Text(monitor.name ?: "Unnamed Monitor")
                }
            } else {
                Text(monitor.name ?: "Unnamed Monitor")
            }
        }

        // Priority badge
        monitor.priority?.let { priority ->
            Span({
                classes(OperationsStyles.monitorPriority)
            }) {
                Text("P$priority")
            }
        }
    }
}

/**
 * A compact inline version of the monitor card for use in lists or tables.
 *
 * @param monitor The monitor to display
 * @param showPriority Whether to show the priority badge
 */
@Composable
fun MonitorCardInline(
    monitor: Monitor,
    showPriority: Boolean = true
) {
    Span({
        style {
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            gap(6.px)
        }
    }) {
        MonitorStatusIndicator(monitor.status, size = 8.px)
        Span { Text(monitor.name ?: "Unnamed") }
        if (showPriority && monitor.priority != null) {
            Span({
                style {
                    fontSize(0.75.em)
                    opacity(0.7)
                }
            }) {
                Text("(P${monitor.priority})")
            }
        }
    }
}
